import React, { useMemo } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { CreditCard, FileText, Receipt, Smartphone, ExternalLink, CheckCircle } from 'lucide-react';

export function PaymentModal({ showPaymentModal, setShowPaymentModal, selectedPayment, processPayment }) {
  return (
    <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Payment Confirmation</DialogTitle>
        </DialogHeader>
        {selectedPayment && (
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
              <p className="text-sm text-slate-600">Description</p>
              <p className="font-semibold">{selectedPayment.description}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
              <p className="text-sm text-slate-600">Amount</p>
              <p className="text-3xl font-bold text-slate-900">&#8377;{selectedPayment.amount}</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> This is a demo payment interface. Razorpay integration pending.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="confirm-payment-button"
                onClick={processPayment}
                className="flex-1 bg-gk-teal hover:bg-gk-teal-hover"
              >
                Confirm Payment
              </Button>
              <Button onClick={() => setShowPaymentModal(false)} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function UPIPaymentModal({
  showUPIModal,
  setShowUPIModal,
  selectedInvoice,
  setSelectedInvoice,
  upiLinks,
  setUpiLinks,
  paymentProcessing,
  handleConfirmUPIPayment,
  openUPIApp,
  copyUPIId
}) {
  return (
    <Dialog open={showUPIModal} onOpenChange={setShowUPIModal}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-green-600" />
            Pay via UPI
          </DialogTitle>
        </DialogHeader>
        {selectedInvoice && upiLinks && (
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-mono text-sm text-slate-600">{selectedInvoice.invoice_number}</p>
                  <p className="font-semibold">{selectedInvoice.description}</p>
                </div>
                <p className="text-2xl font-bold text-slate-900">&#8377;{selectedInvoice.amount.toLocaleString()}</p>
              </div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-700 font-medium mb-2">Pay to UPI ID:</p>
              <div className="flex items-center justify-between bg-white p-2 rounded border">
                <p className="font-mono font-bold text-lg">{upiLinks.upi_vpa}</p>
                <Button size="sm" variant="outline" onClick={copyUPIId}>Copy</Button>
              </div>
              <p className="text-xs text-green-600 mt-2">Payee: {upiLinks.payee_name}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">Quick Pay with:</p>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => openUPIApp(upiLinks.upi_links.gpay_url)} variant="outline" className="h-12 border-2 hover:bg-blue-50 hover:border-blue-300">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-green-500 rounded-full"></div>
                    Google Pay
                  </div>
                </Button>
                <Button onClick={() => openUPIApp(upiLinks.upi_links.phonepe_url)} variant="outline" className="h-12 border-2 hover:bg-gk-bg hover:border-gk-teal/40">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gk-teal rounded-full"></div>
                    PhonePe
                  </div>
                </Button>
                <Button onClick={() => openUPIApp(upiLinks.upi_links.paytm_url)} variant="outline" className="h-12 border-2 hover:bg-blue-50 hover:border-blue-300">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-blue-500 rounded-full"></div>
                    Paytm
                  </div>
                </Button>
                <Button onClick={() => openUPIApp(upiLinks.upi_links.generic_url)} variant="outline" className="h-12 border-2 hover:bg-slate-100">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="w-5 h-5" />
                    Other UPI
                  </div>
                </Button>
              </div>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-800">
                <strong>Transaction ID:</strong> {upiLinks.transaction_id}
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Save this reference for your records. After completing payment in your UPI app, click "Confirm Payment" below.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="confirm-upi-payment-button"
                onClick={handleConfirmUPIPayment}
                disabled={paymentProcessing}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {paymentProcessing ? <>Processing...</> : (
                  <><CheckCircle className="w-4 h-4 mr-2" />I've Paid - Confirm Payment</>
                )}
              </Button>
              <Button
                onClick={() => { setShowUPIModal(false); setSelectedInvoice(null); setUpiLinks(null); }}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ReceiptsModal({
  showReceiptsModal,
  setShowReceiptsModal,
  filteredReceipts,
  receiptFilter,
  setReceiptFilter,
  months,
  years
}) {
  return (
    <Dialog open={showReceiptsModal} onOpenChange={setShowReceiptsModal}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-teal-600" />
            Payment Receipts ({filteredReceipts.length})
          </DialogTitle>
          <DialogDescription>All your payment receipts</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 pb-2 border-b">
          <select
            data-testid="receipt-month-filter"
            value={receiptFilter.month}
            onChange={(e) => setReceiptFilter({ ...receiptFilter, month: e.target.value })}
            className="px-3 py-1.5 border rounded-md text-sm"
          >
            <option value="">All Months</option>
            {months.map((m, i) => <option key={`month-${m}`} value={i}>{m}</option>)}
          </select>
          <select
            data-testid="receipt-year-filter"
            value={receiptFilter.year}
            onChange={(e) => setReceiptFilter({ ...receiptFilter, year: e.target.value })}
            className="px-3 py-1.5 border rounded-md text-sm"
          >
            <option value="">All Years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {(receiptFilter.month !== '' || receiptFilter.year !== '') && (
            <Button size="sm" variant="ghost" onClick={() => setReceiptFilter({ month: '', year: '' })}>Clear</Button>
          )}
        </div>
        <div className="space-y-4 py-2">
          {filteredReceipts.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No receipts found</p>
          ) : (
            filteredReceipts.map((receipt) => (
              <div
                key={receipt.id}
                data-testid={`receipt-item-${receipt.id}`}
                className="p-4 bg-emerald-50 rounded-lg border-2 border-emerald-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-mono font-bold text-lg text-emerald-700">{receipt.receipt_number}</p>
                    <p className="text-sm text-slate-600">Invoice: {receipt.invoice_number}</p>
                  </div>
                  <Badge className="bg-emerald-600">PAID</Badge>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Description</p>
                    <p className="font-semibold">{receipt.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-500">Amount</p>
                    <p className="font-bold text-xl text-emerald-700">&#8377;{receipt.amount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Payment Method</p>
                    <p className="font-semibold">{receipt.payment_method}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-500">Paid On</p>
                    <p className="font-semibold">{new Date(receipt.paid_at).toLocaleDateString()}</p>
                  </div>
                </div>
                {receipt.transaction_id && (
                  <div className="mt-3 pt-3 border-t border-emerald-200">
                    <p className="text-xs text-slate-500">
                      Transaction ID: <span className="font-mono">{receipt.transaction_id}</span>
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function InvoicesModal({
  showInvoicesModal,
  setShowInvoicesModal,
  filteredInvoices,
  invoiceFilter,
  setInvoiceFilter,
  months,
  years,
  totalDue,
  paidInvoices,
  handlePayInvoice
}) {
  const totalPaid = useMemo(() => paidInvoices.reduce((s, i) => s + i.amount, 0), [paidInvoices]);

  return (
    <Dialog open={showInvoicesModal} onOpenChange={setShowInvoicesModal}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            Invoices & Payments ({filteredInvoices.length})
          </DialogTitle>
          <DialogDescription>&#8377;{totalDue.toLocaleString()} due | &#8377;{totalPaid.toLocaleString()} paid</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 pb-2 border-b">
          <select
            data-testid="invoice-month-filter"
            value={invoiceFilter.month}
            onChange={(e) => setInvoiceFilter({ ...invoiceFilter, month: e.target.value })}
            className="px-3 py-1.5 border rounded-md text-sm"
          >
            <option value="">All Months</option>
            {months.map((m, i) => <option key={`month-${m}`} value={i}>{m}</option>)}
          </select>
          <select
            data-testid="invoice-year-filter"
            value={invoiceFilter.year}
            onChange={(e) => setInvoiceFilter({ ...invoiceFilter, year: e.target.value })}
            className="px-3 py-1.5 border rounded-md text-sm"
          >
            <option value="">All Years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {(invoiceFilter.month !== '' || invoiceFilter.year !== '') && (
            <Button size="sm" variant="ghost" onClick={() => setInvoiceFilter({ month: '', year: '' })}>Clear</Button>
          )}
        </div>
        <div className="space-y-3 py-2">
          {filteredInvoices.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No invoices found</p>
          ) : (
            filteredInvoices.map((invoice) => (
              <div
                key={invoice.id}
                data-testid={`invoice-modal-${invoice.id}`}
                className={`p-4 rounded-lg border-2 ${
                  invoice.status === 'paid' ? 'bg-emerald-50 border-emerald-200' :
                  invoice.status === 'overdue' ? 'bg-red-50 border-red-300' :
                  'bg-amber-50 border-amber-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-mono text-sm font-bold">{invoice.invoice_number}</p>
                      <Badge className={
                        invoice.status === 'paid' ? 'bg-emerald-600' :
                        invoice.status === 'overdue' ? 'bg-red-600' :
                        'bg-amber-600'
                      }>
                        {invoice.status.toUpperCase()}
                      </Badge>
                      {invoice.category && <Badge variant="outline">{invoice.category}</Badge>}
                    </div>
                    <p className="font-semibold text-sm">{invoice.description}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {invoice.status === 'paid' ? `Paid: ${new Date(invoice.paid_at).toLocaleDateString()}` : `Due: ${new Date(invoice.due_date).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-slate-900">&#8377;{invoice.amount.toLocaleString()}</p>
                    {(invoice.status === 'pending' || invoice.status === 'overdue') && (
                      <Button data-testid={`pay-modal-${invoice.id}`} onClick={() => { setShowInvoicesModal(false); handlePayInvoice(invoice); }} className="bg-green-600 hover:bg-green-700 mt-2" size="sm">
                        <Smartphone className="w-3.5 h-3.5 mr-1" /> Pay
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentsDueModal({
  showPaymentsDueModal,
  setShowPaymentsDueModal,
  pendingInvoices,
  totalDue,
  handlePayInvoice
}) {
  return (
    <Dialog open={showPaymentsDueModal} onOpenChange={setShowPaymentsDueModal}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-red-600" />
            Payments Due ({pendingInvoices.length})
          </DialogTitle>
          <DialogDescription>&#8377;{totalDue.toLocaleString()} total outstanding</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {pendingInvoices.length === 0 ? (
            <p className="text-center text-emerald-600 py-8 font-semibold">All payments are up to date!</p>
          ) : (
            pendingInvoices.map((invoice) => (
              <div
                key={invoice.id}
                data-testid={`payment-due-${invoice.id}`}
                className={`p-4 rounded-lg border-2 ${invoice.status === 'overdue' ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-200'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-mono text-sm font-bold">{invoice.invoice_number}</p>
                      <Badge className={invoice.status === 'overdue' ? 'bg-red-600' : 'bg-amber-600'}>{invoice.status.toUpperCase()}</Badge>
                      <Badge variant="outline">{invoice.category}</Badge>
                    </div>
                    <p className="font-semibold text-sm">{invoice.description}</p>
                    <p className="text-xs text-slate-500 mt-1">Due: {new Date(invoice.due_date).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-slate-900 mb-2">&#8377;{invoice.amount.toLocaleString()}</p>
                    <Button data-testid={`pay-due-${invoice.id}`} onClick={() => { setShowPaymentsDueModal(false); handlePayInvoice(invoice); }} className="bg-green-600 hover:bg-green-700" size="sm">
                      <Smartphone className="w-4 h-4 mr-1" /> Pay
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
